import { useMemo } from 'react';
import { UIMarket } from '@drift/common';
import { User, FOUR, BigNum, MarketType } from '@drift-labs/sdk';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import useMarketMaxLeverage from 'src/hooks/useMarketMaxLeverages';

/**
 * Calculates max leverage for a perp position, returning the minimum of:
 * 1. User's account health-based max leverage
 * 2. User's personal max leverage cap (if set)
 */
export const useMaxLeverageAvailable = (props: {
	selectedMarket: UIMarket | null;
	currentUserClient: User | undefined;
	maxBaseAvailable: number;
	bufferedMinOrderSize: number;
	freeCollateral: number;
	canEnterHighLeverageMode: boolean;
}) => {
	const {
		selectedMarket,
		currentUserClient,
		maxBaseAvailable,
		bufferedMinOrderSize,
		freeCollateral,
		canEnterHighLeverageMode,
	} = props;

	const { maxLeverage, highLeverageMaxLeverage, hasHighLeverage } =
		useMarketMaxLeverage(MarketType.PERP, selectedMarket?.marketIndex);

	const userMaxLeverageForSelectedMarket = useMarketSettingsStore((s) =>
		s.getMaxLeverageForMarket(selectedMarket?.symbol)
	);

	return useMemo(() => {
		// If the new max base available is less than the minimum order size, set the max trade size to 0
		if (maxBaseAvailable < bufferedMinOrderSize) {
			return 0;
		}

		const defaultMaxLeverage = hasHighLeverage
			? highLeverageMaxLeverage
			: maxLeverage;

		if (!currentUserClient) {
			return defaultMaxLeverage;
		}

		if (freeCollateral === 0) return 0;

		try {
			// Get account health-based max leverage
			const newMaxLeverage = BigNum.from(
				currentUserClient.getMaxLeverageForPerp(
					selectedMarket?.marketIndex ?? 0,
					undefined,
					undefined,
					canEnterHighLeverageMode
				),
				FOUR
			).toNum();

			// Return min of account max and user's custom cap
			return Math.min(
				newMaxLeverage,
				userMaxLeverageForSelectedMarket ?? defaultMaxLeverage
			);
		} catch (e) {
			// little race condition here after wallet disconnect so wrap in try/catch
			return defaultMaxLeverage;
		}
	}, [
		maxLeverage,
		highLeverageMaxLeverage,
		hasHighLeverage,
		selectedMarket?.marketIndex,
		currentUserClient,
		freeCollateral,
		canEnterHighLeverageMode,
		bufferedMinOrderSize,
		maxBaseAvailable,
		userMaxLeverageForSelectedMarket,
	]);
};
